import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { Icon } from '../Icon';
import { getPlanSaveLabel } from '../../shared/plan-helpers';
import styles from './SubscriptionCardV7.css';

const cx = classnames.bind(styles);

function renderSubtitle(subscriptionData) {
    const weeks = subscriptionData.billingPeriod?.value;

    if (subscriptionData.trial) {
        return (
            <>
                <strong>{subscriptionData.trial.price}</strong>
                {' now, then '}
                <strong>{subscriptionData.price}</strong>
                {` every ${weeks} weeks`}
            </>
        );
    }

    return (
        <>
            <strong>{subscriptionData.price}</strong>
            {` every ${weeks} weeks`}
        </>
    );
}

function SubscriptionCardV7({ subscriptionData, selectedSubscriptionData, selectSubscription, isExpired = false, name = 'subscription-plan' }) {
    const isActive = subscriptionData.plan === selectedSubscriptionData.plan;
    const oldPrice = subscriptionData.displayOldPrice ?? subscriptionData.price;
    const newPrice = subscriptionData.trial?.price ?? subscriptionData.price;
    const hasOldPrice = !isExpired && oldPrice !== newPrice;
    const saveLabel = hasOldPrice && !subscriptionData.isDiscountBadgeHidden ? getPlanSaveLabel(subscriptionData) : null;

    return (
        <label className={cx('root', { active: isActive, withOldPrice: hasOldPrice })}>
            <input
                type='radio'
                name={name}
                value={subscriptionData.plan}
                checked={isActive}
                onChange={() => selectSubscription(subscriptionData)}
                className={styles.input}
            />
            {subscriptionData.isPopular && (
                <div className={styles.popularBanner}>
                    <Icon name='starFilled' className={styles.starIcon} />
                    Recommended
                </div>
            )}
            <div className={styles.content}>
                <div className={styles.detailsRow}>
                    <span className={styles.radio}>
                        <span className={styles.radioDot} />
                    </span>
                    <div className={styles.planInfo}>
                        <p className={styles.name}>{subscriptionData.membershipName ?? subscriptionData.name}</p>
                        <p className={styles.subtitle}>{renderSubtitle(subscriptionData)}</p>
                    </div>
                    <div className={styles.priceInfo}>
                        {hasOldPrice && <p className={styles.oldPrice}>{oldPrice}</p>}
                        <p className={cx('newPrice', { expired: isExpired })}>{newPrice}</p>
                        {Boolean(saveLabel) && <span className={styles.saveBadge}>{`SAVE ${saveLabel}`}</span>}
                    </div>
                </div>
                {isActive && (
                    <ul className={styles.features}>
                        <li className={styles.feature}>
                            <span className={styles.featureIcon}><Icon name='checkTick' /></span>
                            <p>{'Personalized questions '}<strong>for 7 life chapters</strong></p>
                        </li>
                        <li className={styles.feature}>
                            <span className={styles.featureIcon}><Icon name='checkTick' /></span>
                            <p><strong>Voice or text</strong>{' answers'}</p>
                        </li>
                        <li className={styles.feature}>
                            <span className={styles.featureIcon}><Icon name='checkTick' /></span>
                            <p>{'Answers become '}<strong>written chapters</strong></p>
                        </li>
                        <li className={styles.feature}>
                            <span className={styles.featureIcon}><Icon name='checkTick' /></span>
                            <p>{'Save as '}<strong>an ebook</strong></p>
                        </li>
                        <li className={styles.feature}>
                            <span className={styles.featureIcon}><Icon name='checkTick' /></span>
                            <p>{'Cancel '}<strong>anytime</strong></p>
                        </li>
                        <li className={styles.feature}>
                            <span className={styles.featureIcon}><Icon name='checkTick' /></span>
                            <p><strong>Keep access</strong>{' to everything you create'}</p>
                        </li>
                    </ul>
                )}
            </div>
        </label>
    );
}

SubscriptionCardV7.propTypes = {
    subscriptionData: PropTypes.object.isRequired,
    selectedSubscriptionData: PropTypes.object.isRequired,
    selectSubscription: PropTypes.func.isRequired,
    isExpired: PropTypes.bool,
    name: PropTypes.string
};

export {
    SubscriptionCardV7
};
